import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

const ProductOptionsContext = createContext();

/**
 * Custom hook to use the product options context.
 * @returns {object} The product options context value.
 */
export const useProductOptions = () => useContext(ProductOptionsContext);

/**
 * ProductOptionsProvider component to provide product options context.
 *
 * @param {object} props - The props object.
 * @param {React.ReactNode} props.children - The children elements to be wrapped by the provider.
 * @param {Array} props.productOptions - The array of product options.
 * @param {boolean}  props.overrideUnrealImages - Override unreal images boolean
 * @param {Function} props.fetchUEImagesFunc - The Fetch UE Images Func
 * @param  {Array} props.ueImages - unreal engine images
 * @param {object} props.showDependencies - Object to control the visibility of the modal.
 * @param {Function} props.setShowDependencies - Function to set the visibility of the modal.
 * @param {Array} props.templateOptions - The array of template options.
 * @returns {JSX.Element} The ProductOptionsProvider component.
 */
export const ProductOptionsProvider = ({
  children,
  productOptions,
  overrideUnrealImages,
  fetchUEImagesFunc,
  ueImages,
  showDependencies,
  setShowDependencies,
  templateOptions,
}) => {
  const value = useMemo(
    () => ({
      productOptions,
      templateOptions,
      overrideUnrealImages,
      fetchUEImagesFunc,
      ueImages,
      showDependencies,
      setShowDependencies,
    }),
    [
      productOptions,
      templateOptions,
      overrideUnrealImages,
      fetchUEImagesFunc,
      ueImages,
      showDependencies,
      setShowDependencies,
    ],
  );
  return (
    <ProductOptionsContext.Provider value={value}>
      {children}
    </ProductOptionsContext.Provider>
  );
};

ProductOptionsProvider.defaultProps = {
  templateOptions: [],
};

ProductOptionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  productOptions: PropTypes.array.isRequired,
  templateOptions: PropTypes.array,
  overrideUnrealImages: PropTypes.bool.isRequired,
  fetchUEImagesFunc: PropTypes.func.isRequired,
  ueImages: PropTypes.array.isRequired,
  showDependencies: PropTypes.shape({
    show: PropTypes.bool,
    node: PropTypes.object,
  }).isRequired,
  setShowDependencies: PropTypes.func.isRequired,
};
